import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../../home/hooks/useInView';

const PriceCalculator = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [largeur, setLargeur] = useState(120);
  const [hauteur, setHauteur] = useState(150);
  const [quantite, setQuantite] = useState(1);
  const [vitrageType, setVitrageType] = useState<'double' | 'triple'>('double');
  const [etage, setEtage] = useState(0);
  const [ascenseur, setAscenseur] = useState(false);

  // Calcul de la surface en m²
  const surfaceUnitaire = (largeur * hauteur) / 10000;
  const surfaceTotale = surfaceUnitaire * quantite;

  // Prix au m² selon le type de vitrage uniquement
  const getPricePerM2 = () => {
    return vitrageType === 'double' ? 150 : 220;
  };

  // Vérifier si le lift est nécessaire
  const needsLift = largeur > 195 || hauteur > 195;
  const liftPrice = needsLift ? 175 : 0;

  // Calcul du supplément étage (seulement si pas de lift)
  const getSupplementEtage = () => {
    if (needsLift || etage === 0) return 0;
    if (ascenseur) {
      return etage * 30; // 30€ par étage avec ascenseur
    } else {
      return etage * 80; // 80€ par étage sans ascenseur
    }
  };

  const pricePerM2 = getPricePerM2();
  const basePrice = Math.round(surfaceTotale * pricePerM2);
  const supplementEtage = getSupplementEtage();
  const fraisDeplacement = 50;
  const fraisPlacement = 90;
  const totalPrice = basePrice + supplementEtage + fraisDeplacement + fraisPlacement + liftPrice;

  // Fonction d'impression du devis
  const handlePrintQuote = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Devis Double Vitrage</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 40px;
              color: #1f2937;
              background: #fff;
            }
            .header {
              border-bottom: 4px solid #0d9488;
              padding-bottom: 20px;
              margin-bottom: 30px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .company-info h1 {
              color: #0d9488;
              font-size: 28px;
              margin-bottom: 8px;
              font-weight: 700;
            }
            .company-info p {
              color: #6b7280;
              font-size: 14px;
              line-height: 1.6;
            }
            .quote-badge {
              background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
              color: white;
              padding: 12px 24px;
              border-radius: 8px;
              font-weight: 700;
              font-size: 18px;
              text-align: center;
            }
            .quote-info {
              background: #f0fdfa;
              padding: 20px;
              border-radius: 12px;
              margin-bottom: 30px;
              border-left: 4px solid #0d9488;
            }
            .quote-info h2 {
              color: #0d9488;
              font-size: 20px;
              margin-bottom: 15px;
              font-weight: 600;
            }
            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 12px;
            }
            .info-item {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
            }
            .info-label {
              color: #6b7280;
              font-weight: 500;
            }
            .info-value {
              color: #1f2937;
              font-weight: 700;
            }
            .price-section {
              background: #fff;
              border: 2px solid #e5e7eb;
              border-radius: 12px;
              padding: 25px;
              margin-bottom: 30px;
            }
            .price-section h3 {
              color: #1f2937;
              font-size: 18px;
              margin-bottom: 20px;
              font-weight: 600;
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 10px;
            }
            .price-row {
              display: flex;
              justify-content: space-between;
              padding: 12px 0;
              border-bottom: 1px solid #f3f4f6;
            }
            .price-row:last-child { border-bottom: none; }
            .price-label { color: #6b7280; }
            .price-value {
              font-weight: 600;
              color: #1f2937;
            }
            .total-row {
              background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
              color: white;
              padding: 20px;
              border-radius: 8px;
              margin-top: 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .total-label {
              font-size: 20px;
              font-weight: 700;
            }
            .total-value {
              font-size: 32px;
              font-weight: 700;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e5e7eb;
              text-align: center;
              color: #6b7280;
              font-size: 12px;
            }
            .notes {
              background: #fffbeb;
              border-left: 4px solid #f59e0b;
              padding: 15px;
              margin-top: 20px;
              border-radius: 8px;
            }
            .notes h4 {
              color: #92400e;
              font-size: 14px;
              margin-bottom: 8px;
              font-weight: 600;
            }
            .notes ul {
              list-style: none;
              padding-left: 0;
            }
            .notes li {
              color: #78350f;
              font-size: 12px;
              padding: 4px 0;
              padding-left: 20px;
              position: relative;
            }
            .notes li:before {
              content: "•";
              position: absolute;
              left: 8px;
              color: #f59e0b;
            }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-info">
              <h1>Réparation Fenêtres Toulouse</h1>
              <p>Toulouse et toute la Haute-Garonne (31)</p>
              <p>Tél: 05 54 54 25 45 | WhatsApp: 07 45 88 33 92</p>
            </div>
            <div class="quote-badge">
              DEVIS<br>ESTIMATIF
            </div>
          </div>

          <div class="quote-info">
            <h2>Informations du Devis</h2>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Date:</span>
                <span class="info-value">${currentDate}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Type de vitrage:</span>
                <span class="info-value">${vitrageType === 'double' ? 'Double vitrage' : 'Triple vitrage'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Dimensions:</span>
                <span class="info-value">${largeur} x ${hauteur} cm</span>
              </div>
              <div class="info-item">
                <span class="info-label">Quantité:</span>
                <span class="info-value">${quantite} ${quantite > 1 ? 'unités' : 'unité'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Surface unitaire:</span>
                <span class="info-value">${surfaceUnitaire.toFixed(2)} m²</span>
              </div>
              <div class="info-item">
                <span class="info-label">Surface totale:</span>
                <span class="info-value">${surfaceTotale.toFixed(2)} m²</span>
              </div>
              ${etage > 0 ? `
              <div class="info-item">
                <span class="info-label">Étage:</span>
                <span class="info-value">${etage}${ascenseur ? ' (avec ascenseur)' : ' (sans ascenseur)'}</span>
              </div>
              ` : ''}
            </div>
          </div>

          <div class="price-section">
            <h3>Détail du Devis</h3>
            <div class="price-row">
              <span class="price-label">Vitrage (${surfaceTotale.toFixed(2)} m² × ${pricePerM2}€/m²)</span>
              <span class="price-value">${basePrice}€</span>
            </div>
            ${supplementEtage > 0 ? `
            <div class="price-row">
              <span class="price-label">Supplément étage ${etage} ${ascenseur ? '(avec ascenseur)' : '(sans ascenseur)'}</span>
              <span class="price-value">${supplementEtage}€</span>
            </div>
            ` : ''}
            ${needsLift ? `
            <div class="price-row">
              <span class="price-label">Lift (dimensions &gt; 195 cm)</span>
              <span class="price-value">${liftPrice}€</span>
            </div>
            ` : ''}
            <div class="price-row">
              <span class="price-label">Frais de déplacement</span>
              <span class="price-value">${fraisDeplacement}€</span>
            </div>
            <div class="price-row">
              <span class="price-label">Frais de placement</span>
              <span class="price-value">${fraisPlacement}€</span>
            </div>
            <div class="total-row">
              <span class="total-label">TOTAL ESTIMATIF</span>
              <span class="total-value">${totalPrice}€</span>
            </div>
          </div>

          <div class="notes">
            <h4>Notes importantes:</h4>
            <ul>
              <li>Ce devis est purement indicatif et ne constitue pas un engagement contractuel</li>
              <li>Le prix final sera confirmé après visite technique sur place</li>
              <li>Devis gratuit et sans engagement</li>
              <li>Intervention rapide sous 2h en cas d'urgence</li>
              <li>Garantie sur tous nos travaux</li>
            </ul>
          </div>

          <div class="footer">
            <p>Réparation Fenêtres Toulouse - Expert en menuiserie et vitrage</p>
            <p>Intervention 24h/24 - 7j/7 en urgence</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-white to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-calculator-line mr-2"></i>
            Calculateur en ligne
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Estimez le <strong className="text-teal-600">Prix de votre Vitrage</strong>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Obtenez une estimation instantanée pour votre projet de double ou triple vitrage
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulaire de calcul */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Configurez votre vitrage</h3>

            <div className="space-y-6">
              {/* Type de vitrage */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Type de vitrage
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setVitrageType('double')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all cursor-pointer ${
                      vitrageType === 'double'
                        ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Double vitrage
                  </button>
                  <button
                    type="button"
                    onClick={() => setVitrageType('triple')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all cursor-pointer ${
                      vitrageType === 'triple'
                        ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Triple vitrage
                  </button>
                </div>
              </div>

              {/* Largeur */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Largeur (cm): <span className="text-teal-600">{largeur} cm</span>
                </label>
                <input
                  type="range"
                  min="50"
                  max="300"
                  value={largeur}
                  onChange={(e) => setLargeur(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>50 cm</span>
                  <span>300 cm</span>
                </div>
              </div>

              {/* Hauteur */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Hauteur (cm): <span className="text-teal-600">{hauteur} cm</span>
                </label>
                <input
                  type="range"
                  min="50"
                  max="300"
                  value={hauteur}
                  onChange={(e) => setHauteur(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>50 cm</span>
                  <span>300 cm</span>
                </div>
              </div>

              {/* Quantité */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Quantité: <span className="text-teal-600">{quantite}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={quantite}
                  onChange={(e) => setQuantite(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1</span>
                  <span>10</span>
                </div>
              </div>

              {/* Étage */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Étage: <span className="text-teal-600">{etage === 0 ? 'Rez-de-chaussée' : `${etage}e étage`}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={etage}
                  onChange={(e) => setEtage(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>RDC</span>
                  <span>10e</span>
                </div>
              </div>

              {/* Ascenseur */}
              {etage > 0 && (
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                  <label className="text-sm font-semibold text-gray-700">
                    Ascenseur disponible ?
                  </label>
                  <button
                    type="button"
                    onClick={() => setAscenseur(!ascenseur)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                      ascenseur ? 'bg-teal-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        ascenseur ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}

              {/* Alerte lift */}
              {needsLift && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start">
                  <i className="ri-alert-line text-orange-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-orange-900 mb-1">
                      Lift nécessaire
                    </p>
                    <p className="text-xs text-orange-700">
                      Vos dimensions dépassent 195 cm. Un lift sera nécessaire (+175€)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Résultat du calcul */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-xl p-8 text-white"
          >
            <h3 className="text-2xl font-bold mb-6">Estimation du prix</h3>

            <div className="space-y-4 mb-8">
              <div className="bg-white/10 rounded-lg p-4">
                <div className="text-sm opacity-90 mb-1">Surface totale</div>
                <div className="text-2xl font-bold">{surfaceTotale.toFixed(2)} m²</div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-90">Vitrage ({pricePerM2}€/m²)</span>
                  <span className="font-semibold">{basePrice}€</span>
                </div>

                {supplementEtage > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm opacity-90">
                      Supplément étage {etage} {ascenseur ? '(ascenseur)' : '(escalier)'}
                    </span>
                    <span className="font-semibold">{supplementEtage}€</span>
                  </div>
                )}

                {needsLift && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm opacity-90">Lift (dimensions &gt; 195 cm)</span>
                    <span className="font-semibold">{liftPrice}€</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-90">Frais de déplacement</span>
                  <span className="font-semibold">{fraisDeplacement}€</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-90">Frais de placement</span>
                  <span className="font-semibold">{fraisPlacement}€</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">TOTAL ESTIMATIF</span>
                  <span className="text-4xl font-bold">{totalPrice}€</span>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <i className="ri-information-line text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                <p className="text-sm opacity-90">
                  Ce prix est une estimation. Le tarif final sera confirmé après une visite technique gratuite sur place.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={handlePrintQuote}
                className="w-full bg-white text-teal-600 px-6 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-printer-line text-xl mr-2"></i>
                Imprimer le devis
              </button>

              <a
                href="tel:0554542545"
                className="w-full bg-orange-500 text-white px-6 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl mr-2"></i>
                Appeler pour confirmer
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PriceCalculator;
